import type { Typography } from "@circleco/compass/components/Typography";
import type { ComponentPropsWithRef } from "react";

type CompassColor = NonNullable<
  ComponentPropsWithRef<typeof Typography>["color"]
>;

const COLOR_MAP: Record<string, CompassColor> = {
  // --- UIKit semantic tokens ---
  "text-dark": "primary",
  "text-darkest": "primary",
  "text-default": "secondary",
  "text-primary": "primary",
  "text-very-dark": "primary",
  "text-secondary": "secondary",
  "text-tertiary": "tertiary",
  "text-subtle": "tertiary",
  "text-light": "tertiary",
  "text-lightest": "tertiary",
  "text-link": "link",
  "text-white": "inverse",
  "text-dark-inverse": "inverse",
  "text-inverse": "inverse",
  "text-feature-dark": "inverse",
  "text-disabled": "disabled",
  "text-selector-active": "primary",
  "text-timestamp": "secondary",
  "text-badge": "inverse",
  "text-stroke-foreground": "primary",
  "text-live": "primary",
  "text-live-medium": "disabled",
  "text-inherit": "current",
  "text-current": "current",

  // --- Status / semantic colors ---
  "text-danger": "danger",
  "text-feedback-error": "danger",
  "text-red-500": "danger",
  "text-v2-danger": "danger",
  "text-success": "success",
  "text-v2-success": "success",
  "text-green-success": "success",
  "text-green-600": "success",
  "text-green-900": "success",
  "text-messaging-info-blue": "info",
  "text-date-blue": "info",

  // --- Tailwind gray scale ---
  "text-gray-100": "inverse",
  "text-gray-200": "inverse",
  "text-gray-300": "tertiary",
  "text-gray-400": "tertiary",
  "text-gray-500": "secondary",
  "text-gray-600": "secondary",
  "text-gray-800": "primary",
  "text-gray-900": "primary",
  "text-slate-400": "tertiary",
  "text-slate-700": "secondary",
  "text-zinc-300": "tertiary",
  "text-white/60": "tertiary",

  // --- Hardcoded hex colors ---
  // Arbitrary hex values (text-[#...]) are intentionally NOT mapped here.
  // They flow through extractExtraClasses → className and override the CVA
  // default via tailwind-merge, preserving the exact color the caller chose.

  // --- Branded / component-specific ---
  "text-circle-button": "inverse",
};

const PSEUDO_PREFIXES = [
  "dark:",
  "hover:",
  "group-hover:",
  "focus:",
  "active:",
];

const hasPseudoPrefix = (token: string) =>
  PSEUDO_PREFIXES.some(p => token.startsWith(p));

export const mapColor = (color?: string): CompassColor | undefined => {
  if (!color) return undefined;

  const directMatch = COLOR_MAP[color];
  if (directMatch) return directMatch;

  const tokens = color.split(" ").filter(Boolean);
  for (const token of tokens) {
    if (!hasPseudoPrefix(token)) {
      const match = COLOR_MAP[token];
      if (match) return match;
    }
  }

  return undefined;
};

// Pseudo-class tokens (hover:, group-hover:, etc.) that contain mapped color
// tokens are passed through as className.  Whether they survive depends on
// Compass's allowedClassesFor whitelist — update the whitelist there if
// interactive color states need preserving.
export const extractExtraClasses = (color?: string): string | undefined => {
  if (!color) return undefined;

  const extras = color
    .split(" ")
    .filter(t => Boolean(t) && !t.startsWith("dark:") && !COLOR_MAP[t]);

  return extras.length > 0 ? extras.join(" ") : undefined;
};
